import { PostQueuePosition, PostSource, PostTiming } from '../models/graphql';
import { PostEditorContextEnum, PostEditorRecurringIntervalType, PostEditorState } from '../models/post-editor';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext, OnClosePostEditorAction } from '../providers/AppProvider';
import { usePostLinkSettings } from './usePostLinkSettings';
import { initialPostEditorStateBuilder } from '../modules/PostEditor/helpers/initialStateBuilder';
import { showToast } from '../helpers/showToast';
import { PostEditorPublicAction } from '../modules/PostEditor/states/PostEditorActions';
import { useMediaUpload } from './useMediaUpload';
import { useSelectedOrganization } from './useSelectedOrganization';
import { BeaconContext } from '../providers/BeaconProvider';
import { PostEditorErrorKeys } from '../models/validator-props';
import { ErrorMessageSeverity } from '../constants/form';
import { SelectedSocialProfileValue } from '../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { OnboardingContext } from '../modules/Onboarding/states/OnboardingProvider';
import { InitialPostEditorContent } from '../modules/Onboarding/constants/content';
import { usePostEditorWorkspaceSettings } from './usePostEditorWorkspaceSettings';
import { useSubscriptionStatus } from './useSubscriptionStatus';
import { useOrganizationPlan } from './useOrganizationPlan';

// NOTE!!! in we provide links that should be matched in text, they must be the same with protocol and query params.
// we can only match the exact links. e.g. text: some text with google.com; link: https://google.com won't mach.
// we always need to have the both same, with valid protocol prefixed urls:
// text: some text with https://google.com; link: https://google.com
export type NewPostContentType = {
  texts?: string[];
  link?: string;
  uploadOnSubmitImageUrls?: string[];
  uploadImmediatelyImageUrls?: string[];
};

export type NewPostTimingType =
  | NewPostTimingQueueType
  | NewPostTimingRecurringType
  | NewPostTimingScheduleType
  | NewPostTimingPostNowType;

export type NewPostTimingEvergreenType =
  | {
      evergreen: true;
      expiryMaxRepeats?: number;
      expiryDate?: string; //ISO-8601 encoded date string
    }
  | {
      evergreen?: never;
      expiryMaxRepeats?: never;
      expiryDate?: never;
    };

export type NewPostTimingQueueType = {
  postTiming: PostTiming.QUEUE;
  queuePosition?: PostQueuePosition;
} & NewPostTimingEvergreenType;

export type NewPostTimingRecurringType = {
  postTiming: PostTiming.RECURRING;
  date: string; //ISO-8601
  recurringInterval: PostEditorRecurringIntervalType;
  recurringValue: number;
  expiryMaxRepeats?: number;
  expiryDate?: string; //ISO-8601 encoded date string
};

export type NewPostTimingScheduleType = {
  postTiming: PostTiming.SCHEDULE;
  date: string[]; //array of ISO-8601
} & NewPostTimingEvergreenType;

export type NewPostTimingPostNowType = {
  postTiming: PostTiming.NOW;
} & NewPostTimingEvergreenType;

export type NewPostParamsType = {
  source: PostSource;
  groupContent?: NewPostContentType;
  groupTiming?: NewPostTimingType;
  selectedProfiles?: SelectedSocialProfileValue[];
  selectedCategoryId?: string;
  onClose?: OnClosePostEditorAction;
  initialState?: PostEditorState;
  initialValidation?: {
    severity: ErrorMessageSeverity;
    keys?: PostEditorErrorKeys[];
  };
};

type EditPostParamsType = {
  postId: string | number;
};

export type OpenNewPostType = (params: NewPostParamsType) => void;

//type OpenEditPostType = (params: EditPostParamsType) => void;

type OpeningParamsType =
  | { type: PostEditorContextEnum.new; params: NewPostParamsType }
  | { type: PostEditorContextEnum.edit; params: EditPostParamsType };

type UseOpenPostEditorResultType = {
  openNewPost: OpenNewPostType;
};
export const useOpenPostEditor = (): UseOpenPostEditorResultType => {
  const { state, showOnboarding } = useContext(OnboardingContext);
  const { progress, demoCategoryId, selectedProfiles: demoProfiles } = state;
  const postEditorStep = progress.find(step => step.key.includes('post_editor') && step.route === '');
  const openingPostEditorForTheFirstTime = showOnboarding && postEditorStep && !postEditorStep.completed;
  const [postEditorOpenParams, setPostEditorOpenParams] = useState<OpeningParamsType | undefined>(undefined);
  const [openPostEditorOnLinkSettingsLoaded, setOpenPostEditorOnLinkSettingsLoaded] = useState(false);
  const { setPostEditorContext, setSuspendedModalOpen, setLimitReachedModalState } = useContext(AppContext);
  const postLinkSettings = usePostLinkSettings();
  const { queueUploads } = useMediaUpload();
  const { selectedWorkspace } = useSelectedOrganization();
  const { beacon } = useContext(BeaconContext);
  const workspaceSettings = usePostEditorWorkspaceSettings();
  const { isTemporallySuspended } = useSubscriptionStatus();
  const { canAddPosts } = useOrganizationPlan();

  const openNewPost: OpenNewPostType = useCallback(
    params => {
      const payloads: PostEditorPublicAction[] = [];
      setPostEditorOpenParams({ type: PostEditorContextEnum.new, params: params });
      const {
        source,
        groupContent,
        onClose,
        initialState,
        initialValidation,
        selectedCategoryId,
        selectedProfiles,
        groupTiming
      } = params;

      if (!selectedWorkspace || !workspaceSettings) {
        return;
      }

      if (isTemporallySuspended) {
        setSuspendedModalOpen(true);
        return;
      } else if (!canAddPosts(1)) {
        setLimitReachedModalState({ isOpen: true, type: 'posts' });
        return;
      }

      // If we are passing in a link, but the link settings aren't loaded, we need to create an intent to open the editor.
      if (groupContent?.link && postLinkSettings.loading) {
        setOpenPostEditorOnLinkSettingsLoaded(true);
        return;
      }

      let editorState = initialPostEditorStateBuilder.withSource(source, initialState);
      const { texts: onboardingTexts, link: onboardingLink } = InitialPostEditorContent;
      if (groupContent?.texts) {
        editorState = initialPostEditorStateBuilder.withTexts(
          groupContent.texts,
          groupContent.link ? { url: groupContent.link, linkSettings: postLinkSettings } : undefined,
          editorState
        );
      } else if (openingPostEditorForTheFirstTime && onboardingTexts) {
        editorState = initialPostEditorStateBuilder.withTexts(
          onboardingTexts,
          onboardingLink ? { url: onboardingLink, linkSettings: postLinkSettings } : undefined,
          editorState
        );
      }
      if (groupContent?.link) {
        editorState = initialPostEditorStateBuilder.withLink(groupContent.link, postLinkSettings, editorState);
      } else if (openingPostEditorForTheFirstTime && onboardingLink) {
        editorState = initialPostEditorStateBuilder.withLink(onboardingLink, postLinkSettings, editorState);
      }
      if (groupContent?.uploadOnSubmitImageUrls) {
        editorState = initialPostEditorStateBuilder.withExternalImages(
          groupContent.uploadOnSubmitImageUrls,
          false,
          editorState
        );
        payloads.push({
          type: 'mediaAddFromExternalDeferred',
          payload: { externalMedia: [editorState.postGroups[0].groupPostAttributes.content.media] }
        });
      }
      // TODO we never set the vary media option from the workspace settings, this should be done here
      if (groupContent?.uploadImmediatelyImageUrls) {
        editorState = initialPostEditorStateBuilder.withExternalImages(
          groupContent.uploadImmediatelyImageUrls,
          true,
          editorState
        );
        payloads.push({
          type: 'mediaUploadExternalCallback',
          payload: {
            context: 'url',
            urls: groupContent.uploadImmediatelyImageUrls,
            selectedWorkspaceId: selectedWorkspace.id,
            queueUploads,
            customizeIndex: undefined,
            postGroupIndex: 0,
            beacon
          }
        });
      }
      if (initialValidation) {
        editorState = initialPostEditorStateBuilder.withInitialValidation(
          initialValidation.severity,
          initialValidation.keys,
          editorState
        );
      }
      if (selectedCategoryId) {
        editorState = initialPostEditorStateBuilder.withInitialCategory(selectedCategoryId, editorState);
      } else if (openingPostEditorForTheFirstTime) {
        editorState = initialPostEditorStateBuilder.withInitialCategory(demoCategoryId, editorState);
      }
      if (selectedProfiles) {
        editorState = initialPostEditorStateBuilder.withInitialProfiles(selectedProfiles, editorState);
      } else if (openingPostEditorForTheFirstTime) {
        editorState = initialPostEditorStateBuilder.withInitialProfiles(demoProfiles, editorState);
      }

      if (groupTiming) {
        editorState = initialPostEditorStateBuilder.withPostTiming(workspaceSettings, groupTiming, editorState);
      }

      setPostEditorContext({
        type: PostEditorContextEnum.new,
        initialState: editorState,
        onClose,
        payload: payloads
      });
    },
    [
      beacon,
      demoCategoryId,
      demoProfiles,
      openingPostEditorForTheFirstTime,
      postLinkSettings,
      queueUploads,
      selectedWorkspace,
      setPostEditorContext,
      workspaceSettings,
      isTemporallySuspended,
      setSuspendedModalOpen,
      canAddPosts,
      setLimitReachedModalState
    ]
  );

  // This opens the post editor after the link settings data is loaded.
  // This query & loading state should really belong in the post editor itself.
  useEffect(() => {
    if (openPostEditorOnLinkSettingsLoaded && (!postLinkSettings.loading || postLinkSettings.error)) {
      setOpenPostEditorOnLinkSettingsLoaded(false);
      if (!postEditorOpenParams || postLinkSettings.error) {
        showToast({
          summary: 'Post Editor Error',
          detail: 'Sorry, something went wrong opening the post editor',
          severity: 'error'
        });
        // TODO Client log reason why.
        return;
      }
      switch (postEditorOpenParams.type) {
        case PostEditorContextEnum.new:
          openNewPost(postEditorOpenParams.params);
          break;
        case PostEditorContextEnum.edit:
          // TODO
          //openEditPost(postEditorOpenParams.params);
          break;
      }
    }
  }, [
    openPostEditorOnLinkSettingsLoaded,
    postLinkSettings.loading,
    postLinkSettings.error,
    postEditorOpenParams,
    openNewPost
  ]);

  return {
    openNewPost
  };
};
